/**
 * Doomsworn — GameSystemAdapter implementation
 *
 * d20-derived, lethal and light. Not a 5e hack.
 *
 * KEPT so the existing engine keeps working untouched:
 *   d20 + modifier vs DC · AC · HP · STR DEX CON INT WIS CHA ·
 *   advantage/disadvantage · initiative · conditions · Open5e monsters as-is
 *
 * CUT:
 *   feats · multiclassing · bonus-action economy · reactions · concentration ·
 *   20-level progression · CR math · spell slots everywhere except Cast
 *
 * ADDED:
 *   ATTUNEMENT (7th ability) · the Doom Die · Wear · Roles instead of classes ·
 *   the three magic pillars · the Tilt
 */
import { applyAffinity } from '@machina/core';
import { ATTUNEMENT, CORE_ABILITY_IDS, DOOMSWORN_ABILITIES, getAttunementScore, getModifier, normalizeAbilities, } from './abilities.js';
import { DOOMSWORN_CONDITIONS } from './conditions.js';
import { DOOMSWORN_DM_GUIDANCE } from './guidance.js';
import { DEFAULT_ROLE_ID, DOOMSWORN_ROLES, DOOMSWORN_ROLE_CLASSES, getRole, } from './roles.js';
import { CAST_MAX_SPELL_LEVEL, getCastSlots, resolveCastCheck } from './cast.js';
import { newDoomState, growDoomDie } from './doom.js';
import { doomswornCreationSpec, doomswornSheetSections } from './sheet.js';
// =============================================================================
// ADVANCEMENT
// =============================================================================
/**
 * Ten concessions, not twenty levels. Each step is a cost the character
 * accepted — never a pile of experience points.
 */
export const CONCESSION_CAP = 10;
/** Concessions at which the Doom Die steps up the track: d4 → d6 → d8 → d10 → d12. */
export const DOOM_DIE_GROWTH_STEPS = [3, 5, 7, 9];
/** Slow and flat. There is no 5e proficiency curve to climb. */
export function getProficiencyBonus(step) {
    return Math.min(4, 2 + Math.floor((Math.max(1, step) - 1) / 4));
}
function readData(character) {
    return (character.systemData ?? {});
}
// =============================================================================
// RULES DEFINITION
// =============================================================================
export const DOOMSWORN_RULES = {
    name: 'Doomsworn',
    shortName: 'Doomsworn',
    license: 'Proprietary — private world content. d20 engine layer derived from SRD 5.1 (CC-BY-4.0).',
    publisher: 'Machina',
    // Not a Dungeon Master. The Narrator describes; the engine remembers, and
    // cannot be talked out of what it remembers.
    gamemasterTitle: 'The Narrator',
    abilities: DOOMSWORN_ABILITIES,
    abilityModFormula: 'floor((score - 10) / 2)',
    characterTemplate: {
        // Ten concessions, not twenty levels.
        levelCap: CONCESSION_CAP,
        // Roles are class-shaped, so the existing character pipeline keeps working.
        usesClasses: true,
        // No ancestries. Where you stand in the Indenture is what matters, and that is the Role.
        usesRaces: false,
        // You advance when you accept a cost, not when you kill something.
        progressionType: 'advancement-track',
        // Deliberately no xpTable: there is no XP in this system.
        healthModel: 'hit-points',
        defenseModel: 'armor-class',
    },
    classes: DOOMSWORN_ROLE_CLASSES,
    coreResolution: {
        type: 'd20-roll-high',
        critRange: [20],
        fumbleRange: [1],
    },
    combat: {
        initiativeFormula: '1d20 + dex_mod',
        // One action, your movement, and a free interaction. No bonus actions.
        // No reactions. Their absence here is the rule, not an omission.
        actionEconomy: [
            { id: 'action', name: 'Action', description: 'Attack, Draw a pact, act through a rune, cast, dash, disengage, hide, help, use an object', countPerRound: 1 },
            { id: 'movement', name: 'Movement', description: 'Move up to speed in feet. Leaving a threatened square is free — there are no opportunity attacks.', countPerRound: 1 },
            { id: 'free', name: 'Free Interaction', description: 'One object interaction or a short sentence', countPerRound: 1 },
        ],
        roundDuration: '6 seconds',
        gridBased: true,
        gridUnit: '5ft',
        deathMechanic: 'death-saves',
    },
    conditions: DOOMSWORN_CONDITIONS,
    magic: {
        // Three pillars that behave differently from one another. Described through
        // DmGuidance so the AI GM learns the idioms rather than guessing at 5e defaults.
        type: 'custom',
        castingAbilities: [ATTUNEMENT, 'int'],
        // Cut. Nothing in this system checks concentration.
        concentrationRule: false,
        description: 'Three pillars. PACTS: d20 + Attunement vs DC 10 + (Tier x 3) + floor(Debt / 5). The effect always happens — the roll ' +
            'sets the price in Debt, Liens, or the Power arriving on the map. RUNES: d20 + rune rating vs DC; every success deepens ' +
            'Grip; ratings are bought with permanently sacrificed Attunement and are immune to the Tilt. CAST: ordinary prepared ' +
            'slots over Open5e spells, levels 1-5 only, the one pillar that can fail and the one pillar with slots at all.',
    },
    encounterBalance: {
        // Honest 'none': there is no CR budgeting in this system and no rateEncounter
        // implementation on the adapter, deliberately.
        ratingSystem: 'none',
        description: 'There is no encounter budget. Doomsworn does not rate, scale, or balance encounters against the party. Put in the ' +
            'world what the world contains, telegraph the danger plainly, and let the players choose what to walk into.',
    },
    dmGuidance: DOOMSWORN_DM_GUIDANCE,
};
// =============================================================================
// DICE HELPERS
// =============================================================================
function rollD20() {
    return Math.floor(Math.random() * 20) + 1;
}
function rollDice(count, sides) {
    return Array.from({ length: count }, () => Math.floor(Math.random() * sides) + 1);
}
function getHpStatus(current, max) {
    if (current <= 0)
        return 'unconscious';
    const ratio = current / max;
    if (ratio > 0.75)
        return 'healthy';
    if (ratio > 0.5)
        return 'wounded';
    if (ratio > 0.25)
        return 'bloodied';
    return 'critical';
}
/** Best of STR/DEX. The system is light enough not to care which one you swung with. */
function martialModifier(character) {
    if (!character)
        return 0;
    return Math.max(getModifier(character.abilities.str ?? 10), getModifier(character.abilities.dex ?? 10));
}
/**
 * Does this character have a route into the Cast pillar at all?
 *
 * THE PILLAR GATE IS THE POINT. Pacts are Attunement against a Reckoning DC and
 * runes are a rune rating against a rune DC; neither has a spell save DC and
 * neither is a spell attack. A Blade sworn to a Power is a caster in the
 * fiction and still has no number on this surface, so the magic methods answer
 * `undefined` for them rather than hand back a DC a caller could roll a saving
 * throw against a Draw with.
 */
function hasCastPillar(character) {
    const data = readData(character);
    const role = getRole(data.roleId ?? character.classes?.[0]?.classId ?? '');
    return !!role && role.pillars.includes('cast');
}
/**
 * The one modifier every Cast number is built from: the governing ability plus
 * training.
 *
 * ONE HELPER, because the cast check, the save DC and the spell attack bonus
 * are the same scholar's competence seen three ways. Computing the modifier
 * twice is how they drift, and a save DC that disagrees with the cast check it
 * came from is worse than no save DC — both get printed and only one is ruled.
 *
 * INT by default, because Cast is scholarship out of books and asks nobody for
 * anything. `MagicState.ability` is honoured when it names one of the six d20
 * abilities, since a licensed foreign tradition is content this system does not
 * ship and the caller is the only one who knows what it was taught from.
 * ATTUNEMENT is refused on purpose: letting soul-weight into this roll would
 * make the deniable pillar cost the exact thing it exists to avoid.
 */
function castModifier(character) {
    const declared = character.magic?.ability;
    const abilityId = declared && CORE_ABILITY_IDS.includes(declared) ? declared : 'int';
    return (getModifier(character.abilities[abilityId] ?? 10) + getProficiencyBonus(character.level ?? 1));
}
/**
 * Starting HP for a Role at an advancement step: the Role's hit die plus CON at
 * the first step, then what `levelUp` adds for each concession after it.
 *
 * CON includes the Role's ability package, because `createCharacter` applies
 * the package before it reads CON. At the first step this returns exactly what
 * `createCharacter` would put in `health.max` for the same choices.
 *
 * Undefined without a Role or a CON score. `createCharacter` falls back to the
 * Blade when no Role is named; a suggestion for a Role nobody picked would be
 * a number for the wrong character.
 */
function startingHitPoints(options) {
    const role = typeof options.classId === 'string' ? getRole(options.classId) : undefined;
    const con = options.abilities?.con;
    if (!role || typeof con !== 'number')
        return undefined;
    const conMod = getModifier(con + (role.abilityPackage.con ?? 0));
    const step = Math.max(1, Math.min(CONCESSION_CAP, Number(options.level ?? 1)));
    let hp = Math.max(1, role.hitDie + conMod);
    for (let concession = 2; concession <= step; concession++) {
        hp += Math.max(1, Math.ceil(role.hitDie / 2) + conMod);
    }
    return hp;
}
// =============================================================================
// THE ADAPTER
// =============================================================================
export const doomswornAdapter = {
    id: 'doomsworn',
    name: 'Doomsworn',
    version: '0.1.0',
    rules: DOOMSWORN_RULES,
    // --- Character Lifecycle ---
    createCharacter(options) {
        const role = getRole(String(options.classId ?? DEFAULT_ROLE_ID)) ?? getRole(DEFAULT_ROLE_ID);
        // PCs have a soul-weight: Attunement defaults to 10 for a character. Creatures
        // imported from Open5e never pass through here and stay Attunement-less.
        const abilities = normalizeAbilities({
            [ATTUNEMENT]: 10,
            ...(options.abilities ?? {}),
        });
        for (const [id, bonus] of Object.entries(role.abilityPackage)) {
            abilities[id] = (abilities[id] ?? 10) + (bonus ?? 0);
        }
        const step = Math.max(1, Math.min(CONCESSION_CAP, Number(options.level ?? 1)));
        const conMod = getModifier(abilities.con ?? 10);
        const maxHp = Math.max(1, role.hitDie + conMod);
        const castsFromBooks = role.pillars.includes('cast');
        // Inherited Debt lands on the ledger before play begins. The Power can be
        // named at creation; otherwise the engine holds a placeholder for the House
        // Power the table has not chosen yet.
        const ledger = role.startingDebt
            ? [
                {
                    powerId: String(options.powerId ?? `${role.id}-power`),
                    debt: role.startingDebt,
                    liens: [],
                    callings: [],
                },
            ]
            : [];
        const systemData = {
            roleId: role.id,
            pull: role.pull,
            doom: newDoomState(typeof options.doom === 'string' ? options.doom : ''),
            wear: [],
            ledger,
            runes: [],
            concessions: step - 1,
            concessionLog: [],
        };
        return {
            id: crypto.randomUUID(),
            name: options.name,
            systemId: 'doomsworn',
            abilities,
            level: step,
            classes: [{ classId: role.id, level: step }],
            health: { current: maxHp, max: maxHp, temp: 0 },
            defense: 10 + getModifier(abilities.dex ?? 10),
            speed: 30,
            conditions: [],
            proficiencies: {
                savingThrows: [...role.savingThrows],
                skills: [],
            },
            // `ability` is the well-known `MagicState` key, stamped here so a caller
            // asking for a save DC never has to guess which of the seven abilities
            // Cast runs on. INT: Cast is scholarship, and Attunement belongs to the
            // pillar that costs you something.
            magic: castsFromBooks
                ? { pillar: 'cast', ability: 'int', slots: getCastSlots(step), used: {} }
                : undefined,
            systemData: systemData,
        };
    },
    validateCharacter(character) {
        const errors = [];
        const warnings = [];
        const data = readData(character);
        if (!character.abilities || Object.keys(character.abilities).length === 0) {
            errors.push('Character must have ability scores');
        }
        for (const [ability, score] of Object.entries(character.abilities ?? {})) {
            // Attunement may legitimately be sacrificed all the way to 0 for rune
            // ratings. The mighty are hollow by construction.
            const floorScore = ability === ATTUNEMENT ? 0 : 1;
            if (score < floorScore || score > 30) {
                errors.push(`${ability}: score ${score} out of range (${floorScore}-30)`);
            }
        }
        if (character.level !== undefined && (character.level < 1 || character.level > CONCESSION_CAP)) {
            errors.push(`Advancement step ${character.level} out of range (1-${CONCESSION_CAP} concessions)`);
        }
        if (character.classes && character.classes.length > 1) {
            errors.push('Doomsworn has no multiclassing — a character holds exactly one Role');
        }
        const roleId = data.roleId ?? character.classes?.[0]?.classId;
        if (!roleId) {
            // Warning, not an error: creatures imported from Open5e are valid
            // SystemCharacters and hold no Role. Only PCs stand in the Indenture.
            warnings.push('No Role. Correct for a creature; a PC must hold exactly one Role.');
        }
        else if (!getRole(roleId)) {
            errors.push(`Unknown Role "${roleId}" — expected one of: ${DOOMSWORN_ROLES.map((r) => r.id).join(', ')}`);
        }
        if (character.health.max <= 0) {
            errors.push('Max HP must be positive');
        }
        if (getAttunementScore(character.abilities) === undefined) {
            warnings.push('No Attunement score: this character cannot Draw on a Power, carry a rune, or defend a will-contest. ' +
                'Correct for a creature, almost certainly wrong for a PC.');
        }
        else if (getAttunementScore(character.abilities) === 0) {
            warnings.push('Attunement 0 — there is nothing left of this character for a Power to get hold of.');
        }
        if (!data.doom?.declared) {
            warnings.push('No declared Doom. Every PC writes down their Doom at creation; the GM reads it every scene.');
        }
        return { valid: errors.length === 0, errors, warnings };
    },
    getAbilityModifier(score) {
        return getModifier(score);
    },
    getAbilities() {
        return DOOMSWORN_ABILITIES;
    },
    getClasses() {
        return DOOMSWORN_ROLE_CLASSES;
    },
    getConditions() {
        return DOOMSWORN_CONDITIONS;
    },
    /**
     * Advancement is a CONCESSION: a cost accepted, recorded by name. There are
     * no feats and no ability score improvements, so `choices.feats` and
     * `choices.abilityScoreImprovements` are deliberately ignored — accepting
     * them would quietly reintroduce 5e progression through the side door.
     * `choices.classId` is ignored too: a character holds one Role for life.
     */
    levelUp(character, choices) {
        const data = readData(character);
        const role = getRole(data.roleId ?? choices.classId ?? DEFAULT_ROLE_ID) ?? getRole(DEFAULT_ROLE_ID);
        const step = Math.min(CONCESSION_CAP, (character.level ?? 1) + 1);
        const conMod = getModifier(character.abilities.con ?? 10);
        const hpGain = Math.max(1, Math.ceil(role.hitDie / 2) + conMod);
        const concession = typeof choices.concession === 'string' && choices.concession.trim()
            ? choices.concession.trim()
            : 'an unnamed cost';
        const doom = data.doom ?? newDoomState();
        const nextDoom = DOOM_DIE_GROWTH_STEPS.includes(step)
            ? { ...doom, die: growDoomDie(doom.die) }
            : doom;
        const nextData = {
            roleId: role.id,
            pull: role.pull,
            doom: nextDoom,
            wear: data.wear ?? [],
            ledger: data.ledger ?? [],
            runes: data.runes ?? [],
            concessions: (data.concessions ?? 0) + 1,
            concessionLog: [...(data.concessionLog ?? []), concession],
            tilt: data.tilt,
        };
        return {
            ...character,
            level: step,
            classes: [{ classId: role.id, level: step }],
            health: {
                ...character.health,
                max: character.health.max + hpGain,
                current: character.health.current + hpGain,
            },
            magic: role.pillars.includes('cast')
                ? { ...(character.magic ?? {}), pillar: 'cast', slots: getCastSlots(step) }
                : character.magic,
            systemData: nextData,
        };
    },
    getProficiencyBonus(level) {
        return getProficiencyBonus(level);
    },
    // --- Dice & Resolution ---
    rollCheck(context) {
        const modifier = context.modifier ?? 0;
        // Advantage/disadvantage is kept from the d20 engine and is implemented
        // here rather than left to the caller. Both at once cancel, as they should.
        const advantage = !!context.advantage && !context.disadvantage;
        const disadvantage = !!context.disadvantage && !context.advantage;
        const dice = advantage || disadvantage ? [rollD20(), rollD20()] : [rollD20()];
        const natural = advantage
            ? Math.max(...dice)
            : disadvantage
                ? Math.min(...dice)
                : dice[0];
        const total = natural + modifier;
        let formula = advantage ? '2d20kh1' : disadvantage ? '2d20kl1' : '1d20';
        if (modifier !== 0) {
            formula += modifier > 0 ? ` + ${modifier}` : ` - ${Math.abs(modifier)}`;
        }
        return {
            total,
            dice,
            modifier,
            formula,
            natural,
            meta: {
                type: context.type,
                ability: context.ability,
                skill: context.skill,
                dc: context.dc,
                advantage,
                disadvantage,
                // The Doom Die is added AFTER this roll is seen, by player choice.
                // It is never rolled here.
                doomDieAvailable: true,
            },
        };
    },
    interpretResult(roll, context) {
        const dc = context.dc ?? 10;
        // Pacts never fail. The Reckoning reports a price, not an outcome, and
        // this branch exists so no caller can accidentally narrate a failed Draw.
        if (context.type === 'pact-reckoning') {
            if (roll.natural === 20) {
                return {
                    outcome: 'critical-success',
                    description: 'The Draw lands and the Power is pleased: Debt forgiven, or a boon volunteered with an agenda attached.',
                    meta: { effectHappens: true, pillar: 'pact' },
                };
            }
            if (roll.natural === 1) {
                return {
                    outcome: 'critical-failure',
                    description: 'The Draw lands — and the Power manifests to collect in person. It is on the map. It has a turn.',
                    meta: { effectHappens: true, pillar: 'pact', manifests: true },
                };
            }
            if (roll.total >= dc) {
                return {
                    outcome: 'success',
                    description: `The Draw lands and the account is charged (${roll.total} vs DC ${dc}).`,
                    meta: { effectHappens: true, pillar: 'pact' },
                };
            }
            return {
                outcome: 'partial',
                description: `The Draw lands — it always does — but you could not afford it: doubled Debt and a Lien (${roll.total} vs DC ${dc}).`,
                meta: { effectHappens: true, pillar: 'pact', takesLien: true },
            };
        }
        if (context.type === 'rune-check') {
            const success = roll.total >= dc;
            return {
                outcome: success ? 'success' : 'failure',
                description: success
                    ? `The rune answers (${roll.total} vs DC ${dc}). Grip +1 — it is that much more of you now.`
                    : `The rune does not move (${roll.total} vs DC ${dc}).`,
                meta: { pillar: 'rune', gripDelta: success ? 1 : 0, tiltImmune: true },
            };
        }
        if (roll.natural === 20) {
            return { outcome: 'critical-success', description: `Natural 20 (total ${roll.total} vs DC ${dc}).` };
        }
        if (roll.natural === 1) {
            return { outcome: 'critical-failure', description: `Natural 1 (total ${roll.total} vs DC ${dc}).` };
        }
        if (roll.total >= dc) {
            return { outcome: 'success', description: `Success (${roll.total} vs DC ${dc})` };
        }
        return { outcome: 'failure', description: `Failure (${roll.total} vs DC ${dc})` };
    },
    // --- Combat ---
    combat: {
        rollInitiative(combatant) {
            const dexMod = combatant.character ? getModifier(combatant.character.abilities.dex ?? 10) : 0;
            return rollD20() + dexMod;
        },
        /**
         * One action, your movement, one free interaction. No bonus actions and no
         * reactions exist in this system, so they are not returned as spent-out
         * slots — they are absent.
         */
        getActionEconomy(_combatant) {
            return {
                actions: [
                    {
                        typeId: 'action',
                        remaining: 1,
                        options: ['Attack', 'Draw a Pact', 'Act Through a Rune', 'Cast', 'Dash', 'Disengage', 'Dodge', 'Help', 'Hide', 'Use an Object'],
                    },
                    { typeId: 'movement', remaining: 1, options: ['Move'] },
                    { typeId: 'free', remaining: 1, options: ['Object interaction', 'Say something short'] },
                ],
            };
        },
        /**
         * A string `action` may carry a damage expression ("Longsword 1d8
         * slashing"); the dice and type are read out of it, defaulting to 1d6
         * weapon damage. A {@link CombatAction} says the same things properly, and
         * anything it states explicitly is preferred over the regex — a caller that
         * has already parsed a stat-block action should not have its numbers
         * re-derived from prose. The string path is byte-for-byte the old
         * behaviour, so existing callers see no change.
         *
         * Attack is d20 + best of STR/DEX + proficiency, unless the action states
         * its own `attackBonus`, which is documented as the TOTAL bonus and so
         * replaces that sum rather than adding to it.
         */
        resolveAttack(attacker, target, action) {
            const spec = typeof action === 'string' ? undefined : action;
            // What the regexes read. For a structured action that is its name, plus
            // its damage expression, since either may carry the dice or the type.
            const text = typeof action === 'string'
                ? action ?? ''
                : `${action.name ?? ''} ${action.damageDice ?? ''}`;
            const derivedAtkMod = martialModifier(attacker.character) + getProficiencyBonus(attacker.character?.level ?? 1);
            const atkMod = spec?.attackBonus ?? derivedAtkMod;
            const natural = rollD20();
            const total = natural + atkMod;
            const targetAC = target.character?.defense ?? 10;
            const hit = natural === 20 || (natural !== 1 && total >= targetAC);
            const isCrit = natural === 20;
            const diceMatch = /(\d+)d(\d+)/i.exec(spec?.damageDice ?? text);
            const count = diceMatch ? parseInt(diceMatch[1], 10) : 1;
            const sides = diceMatch ? parseInt(diceMatch[2], 10) : 6;
            const typeMatch = /\b(slashing|piercing|bludgeoning|fire|cold|acid|poison|necrotic|radiant|lightning|thunder|psychic|force)\b/i.exec(text);
            const damageType = spec?.damageType ?? (typeMatch ? typeMatch[1].toLowerCase() : 'weapon');
            // A `damageDice` of "2d6+3" has already had a modifier added by whoever
            // wrote it, so adding the martial modifier on top would pay the swing
            // twice. An explicit flat bonus therefore REPLACES the derived one; an
            // expression with no flat term still gets STR/DEX as it always did.
            const flatMatch = spec?.damageDice ? /\d+d\d+\s*([+-]\s*\d+)/i.exec(spec.damageDice) : null;
            const damageMod = flatMatch
                ? parseInt(flatMatch[1].replace(/\s+/g, ''), 10)
                : martialModifier(attacker.character);
            const roll = {
                total,
                dice: [natural],
                modifier: atkMod,
                formula: `1d20 + ${atkMod}`,
                natural,
            };
            const interpretation = {
                outcome: isCrit ? 'critical-success' : hit ? 'success' : 'failure',
                description: hit
                    ? `Hit (${total} vs AC ${targetAC}${isCrit ? ' — critical' : ''})`
                    : `Miss (${total} vs AC ${targetAC})`,
            };
            // Unchanged crit rule: double the DICE, add the modifier once.
            const damageCount = isCrit ? count * 2 : count;
            const damage = hit
                ? {
                    total: rollDice(damageCount, sides).reduce((a, b) => a + b, 0) + damageMod,
                    type: damageType,
                    dice: `${damageCount}d${sides}`,
                    // Only stated when the action states it. A bare string says nothing
                    // about enchantment, and claiming `magical: false` on its behalf
                    // would let a target's nonmagical-only resistance fire on a hit
                    // nobody has actually described as mundane.
                    ...(spec?.magical === undefined ? {} : { magical: spec.magical }),
                }
                : undefined;
            return { hit, roll, interpretation, damage };
        },
        applyDamage(target, damage) {
            const char = target.character;
            if (!char) {
                return { previousHp: 0, currentHp: 0, status: 'dead', effects: [] };
            }
            // Affinity first, and everything downstream reads the post-affinity
            // number. A creature immune to fire takes 0 from a fireball, which means
            // it does not drop, does not roll death saves and is not offered the Doom
            // Die — keying any of that off the incoming total would have an immune
            // creature dying of damage it never took.
            const { amount: appliedDamage, verdict } = applyAffinity(target.damageAffinity, damage.total, { type: damage.type, ...(damage.magical === undefined ? {} : { magical: damage.magical }) });
            const previousHp = char.health.current;
            const newHp = Math.max(0, previousHp - appliedDamage);
            // Deliberately NOT written back to the character. The engine owns state
            // (ISS-0132): an adapter answers "what would this damage do", and the
            // caller decides whether it happened. previousHp/currentHp below carry
            // everything needed to apply it, so the write was redundant as well as
            // forbidden. Enforced by @machina/system-conformance, which caught this.
            const effects = [];
            if (newHp === 0 && previousHp > 0) {
                effects.push('death-saves');
                if (appliedDamage >= char.health.max) {
                    effects.push('massive-damage');
                }
                // The out this system offers is the Doom Die, and it costs a segment.
                effects.push('doom-die-offer');
            }
            return {
                previousHp,
                currentHp: newHp,
                status: getHpStatus(newHp, char.health.max),
                effects,
                incomingDamage: damage.total,
                appliedDamage,
                affinity: verdict,
            };
        },
    },
    // --- Magic ---
    //
    // Only the Cast pillar goes through this surface, because only the Cast
    // pillar has slots and only the Cast pillar can fail. Pact Reckonings and
    // rune checks are resolved through `resolveReckoning` and `resolveRuneCheck`
    // and must never be routed here.
    magic: {
        getSpellSlots(character) {
            const data = readData(character);
            const role = getRole(data.roleId ?? character.classes?.[0]?.classId ?? '');
            if (!role || !role.pillars.includes('cast')) {
                return {
                    slots: {},
                    meta: {
                        pillar: role?.pillars[0] ?? 'none',
                        note: 'No slots. Pacts and runes have no slot economy — do not ask this character which slot they are spending.',
                    },
                };
            }
            const used = (character.magic?.used ?? {});
            const raw = getCastSlots(character.level ?? 1);
            const slots = {};
            for (const [levelStr, count] of Object.entries(raw)) {
                const level = parseInt(levelStr, 10);
                if (!Number.isNaN(level) && count > 0) {
                    slots[level] = { total: count, used: used[level] ?? 0 };
                }
            }
            return {
                slots,
                // No concentration in this system, so no concentrating field is ever set.
                meta: { pillar: 'cast', maxSpellLevel: CAST_MAX_SPELL_LEVEL },
            };
        },
        validateCast(character, _spellName, level) {
            const data = readData(character);
            const role = getRole(data.roleId ?? character.classes?.[0]?.classId ?? '');
            if (!role || !role.pillars.includes('cast')) {
                return {
                    canCast: false,
                    reason: 'This character does not cast from books. Pacts are resolved with a Reckoning and runes with a rune check — neither uses slots.',
                };
            }
            const spellLevel = level ?? 1;
            if (spellLevel > CAST_MAX_SPELL_LEVEL) {
                return {
                    canCast: false,
                    reason: `Cast tops out at level ${CAST_MAX_SPELL_LEVEL}. Humanity has not got further.`,
                };
            }
            const slots = getCastSlots(character.level ?? 1);
            const used = (character.magic?.used ?? {});
            const available = (slots[spellLevel] ?? 0) - (used[spellLevel] ?? 0);
            if (spellLevel > 0 && available <= 0) {
                return { canCast: false, reason: `No level ${spellLevel} slots remaining` };
            }
            return {
                canCast: true,
                warnings: ['Cast can fail. The slot is spent either way, and the roll is degraded under Chaos.'],
            };
        },
        resolveCast(character, spellName, targets) {
            const data = readData(character);
            // Spell level is carried on the character envelope by the caller when it
            // is known; a bare call is treated as a 1st-level formula.
            const prepared = (character.magic?.prepared ?? {});
            const spellLevel = prepared[spellName] ?? 1;
            const result = resolveCastCheck({
                spellLevel,
                // INT plus training. Cast asks nobody for anything, so Attunement
                // never enters this roll. Shared with the save DC and the spell attack
                // bonus so the three cannot disagree about the same scholar.
                abilityMod: castModifier(character),
                tilt: data.tilt,
            });
            const roll = {
                total: result.total,
                dice: [result.natural],
                modifier: result.total - result.natural,
                formula: `1d20 + int_mod + prof${result.tiltModifier ? ` + tilt(${result.tiltModifier})` : ''}`,
                natural: result.natural,
            };
            return {
                success: result.success,
                roll,
                interpretation: {
                    outcome: result.success ? 'success' : 'failure',
                    description: result.description,
                    meta: { pillar: 'cast', slotSpent: true, tiltModifier: result.tiltModifier },
                },
                effects: result.success
                    ? [
                        `${spellName} takes hold${targets.length ? ` on ${targets.join(', ')}` : ''}.`,
                        'No Debt, no Grip, no soul-cost. Nothing owns you, and nothing noticed — a cast spell leaves no ledger entry anywhere in the cosmos.',
                    ]
                    : [`${spellName} does not take. The slot is gone regardless.`],
            };
        },
        /**
         * 8 + the governing ability + training, and only for a character who
         * casts from books.
         *
         * The `8 +` is the retained d20 engine layer, not a 5e import by the back
         * door: Cast runs Open5e spells as printed, and a spell that reads "DEX
         * saving throw" is asking for the number that engine computes. Doomsworn
         * changes what magic COSTS, not how a d20 is compared to a DC.
         *
         * Not to be confused with `castDC(spellLevel)`, which is what the caster
         * rolls against to make the formula hold at all. That check asks whether
         * the spell happens; this number is what a victim rolls against once it
         * has. A Cast spell therefore clears two bars, and the second one is this.
         */
        getSpellSaveDC(character) {
            if (!hasCastPillar(character))
                return undefined;
            return 8 + castModifier(character);
        },
        /**
         * The same competence, aimed rather than resisted.
         *
         * Answerable for exactly the reason `listSpellAttacks` is not: an Open5e
         * spell that calls for a ranged spell attack brings its own attack roll,
         * and this is the modifier Doomsworn adds to it.
         */
        getSpellAttackBonus(character) {
            if (!hasCastPillar(character))
                return undefined;
            return castModifier(character);
        },
        // `listSpellAttacks` is deliberately absent.
        //
        // Doomsworn ships no spells. Cast is "Open5e spells filtered to levels
        // 1-5", and that content lives outside this package — the system owns the
        // slot table, the cast check and the ceiling, and owns not one spell's
        // damage expression. Enumerating attacks would mean inventing dice for
        // spells the system has never seen, which is the failure the optional
        // methods exist to prevent.
        //
        // The other two pillars make the omission structural rather than a gap
        // waiting to be filled: a Draw always lands and a rune answers or does
        // not, so neither is an attack roll at all. There is no list here to
        // return, and a caller should name the spell itself.
    },
    // --- Encounter Balance ---
    //
    // Deliberately absent. CR math is cut from this system: there is no encounter
    // budget to compute and no honest implementation of rateEncounter to give.
    // `rules.encounterBalance.ratingSystem` is 'none' and callers should branch
    // on the missing `balance` surface rather than receive a fabricated rating.
    // --- Character Surface ---
    //
    // Declared here so the generic wizard and sheet can render Attunement and the
    // three pillars without knowing this system exists. See `./sheet.ts`.
    characterSurface: {
        getCreationSpec: doomswornCreationSpec,
        getSheetSections: doomswornSheetSections,
        getStartingHealth: startingHitPoints,
    },
    // --- DM Guidance ---
    getDmGuidance() {
        return DOOMSWORN_DM_GUIDANCE;
    },
};
//# sourceMappingURL=doomsworn-adapter.js.map